import { ChannelNodeTypeMap, ChannelType, NODE_CONFIGS } from 'modules/workflows/constants';
import {
  Channel,
  ChannelOptNodeData,
  ChildNode,
  MetricDefinitionsBySendAction,
  NodeResource,
} from 'modules/workflows/types';
import sendNodeUtils from '../sendNodeUtils';
import { ChildNodeIcon, childNodesStyle } from './channelOptUtils.constants';

type ResourceMap = Record<string | number, NodeResource>;

/**
 * Creates a map of resources keyed by campaign id for easy lookup
 */
const keyResourcesByCampaignId = (resources: NodeResource[] = []): ResourceMap => {
  return resources.reduce(
    (a: ResourceMap, r) => (r?.campaign?.id ? { ...a, [r.campaign.id]: r } : a),
    {},
  );
};

/**
 * Constructs a flowchart child node from the channel object given by the backend
 */
export const constructChildNode = (
  item: Channel,
  resource?: NodeResource,
): ChildNode | undefined => {
  const { key, value = {} } = item ?? {};
  const nodeType = ChannelNodeTypeMap[key];

  if (nodeType) {
    const { nodeData = {}, ...node } = NODE_CONFIGS[nodeType]?.data ?? {};
    return { ...node, nodeData: { ...nodeData, ...value, resource } };
  }
};

/**
 * Construct flowchart child nodes from the backend channels
 */
export const constructChildNodes = (
  channels?: Channel[],
  { resources }: NodeResource = {},
): ChildNode[] => {
  const resourceMap = keyResourcesByCampaignId(resources);

  return (channels ?? []).reduce((acc: ChildNode[], item: Channel) => {
    const resource = resourceMap[item?.value?.campaignId as number];
    const childNode = constructChildNode(item, resource);
    return childNode ? [...acc, childNode] : acc;
  }, []);
};

interface ParseChildNodeArgs {
  isQuietHoursFeatureEnabled?: boolean;
  isMetricsSelectionEnabled?: boolean;
  selectedMetrics?: MetricDefinitionsBySendAction;
}

/**
 * Parses a child node so that it includes segments, analytics and other information
 */
export const parseChildNode = (
  opts: ParseChildNodeArgs,
  { nodeData = {}, type, ...node }: ChildNode = {},
): ChildNode => {
  const options = { ...childNodesStyle, icon: ChildNodeIcon[type] };
  const textSegments = sendNodeUtils.getTextSegments(nodeData, type, opts);
  return { ...node, type, options, nodeData: { ...nodeData, textSegments } };
};

/**
 * Parses child nodes so that they have the correct structure for the flowchart
 */
export const parseChildNodes = (
  opts: ParseChildNodeArgs = {},
  childNodes?: ChildNode[],
): ChildNode[] => {
  return (childNodes ?? []).map((childNode) => parseChildNode(opts, childNode));
};

/**
 * Constructs a channel from the childNode -- likely for saving
 */
export const constructChannel = (childNode: ChildNode): Channel | undefined => {
  const channelType = ChannelNodeTypeMap[childNode?.type] as string;
  if (channelType) {
    const { campaignId, templateId } = childNode.nodeData ?? {};
    return { key: channelType as ChannelType, value: { campaignId, templateId } };
  }
};

/**
 * Converts an array of child nodes to its corresponding, savable channel format
 */
export const constructChannels = (childNodes?: ChildNode[]): Channel[] => {
  if (Array.isArray(childNodes)) {
    return childNodes.reduce((acc: Channel[], childNode) => {
      const channelItem = constructChannel(childNode);
      return channelItem ? [...acc, channelItem] : acc;
    }, []);
  }

  return [];
};

/** Gets the description text for the node */
export const getDescription = ({ childNodes = [] }: ChannelOptNodeData) => {
  const numChannels = childNodes?.length ?? 0;
  return `${numChannels} channel${numChannels !== 1 ? 's' : ''}`;
};

export default {
  constructChannel,
  constructChannels,
  constructChildNode,
  constructChildNodes,
  getDescription,
  parseChildNode,
  parseChildNodes,
};
